export type CompanyAccountTypeFilter = "all" | "company" | "private"
export type CompanyStatusFilter = "all" | "active" | "disabled" | "deleted"
export type CompanyRegistrationSourceFilter = "all" | "backoffice" | "signup"
export type CompanyDirectorySort =
	| "created_desc"
	| "created_asc"
	| "name_asc"
	| "name_desc"
	| "balance_desc"
	| "balance_asc"

export type CompanyDirectoryFilter = {
	query: string
	accountType: CompanyAccountTypeFilter
	status: CompanyStatusFilter
	registrationSource: CompanyRegistrationSourceFilter
	providerCode: string
	sort: CompanyDirectorySort
}

export interface CompanyDirectoryEntry {
	uid: string
	name: string
	number?: string | null
	vat_number?: string | null
	balance?: string | number | null
	created_at: string
	status: string
	type?: string
	registration_source?: string | null
	referral_code?: string | null
	admins?: Array<{ name?: string | null; email?: string | null }>
	issuers?: Array<{ provider_code?: string | null }>
}

export const DEFAULT_COMPANY_DIRECTORY_FILTER: CompanyDirectoryFilter = {
	query: "",
	accountType: "all",
	status: "all",
	registrationSource: "all",
	providerCode: "all",
	sort: "created_desc"
}

const normalize = (value: unknown) => String(value ?? "").trim().toLocaleLowerCase()

const getAccountType = (company: CompanyDirectoryEntry): Exclude<CompanyAccountTypeFilter, "all"> =>
	company.type === "private" ? "private" : "company"

const compareText = (left: unknown, right: unknown) =>
	String(left ?? "").localeCompare(String(right ?? ""), undefined, { sensitivity: "base" })

const getCreatedTime = (value: string) => {
	const timestamp = Date.parse(value)
	return Number.isNaN(timestamp) ? 0 : timestamp
}

const compareCompanies = (
	left: CompanyDirectoryEntry,
	right: CompanyDirectoryEntry,
	sort: CompanyDirectorySort
) => {
	let result = 0
	switch (sort) {
		case "name_asc":
			result = compareText(left.name, right.name)
			break
		case "name_desc":
			result = compareText(right.name, left.name)
			break
		case "balance_asc":
			result = Number(left.balance || 0) - Number(right.balance || 0)
			break
		case "balance_desc":
			result = Number(right.balance || 0) - Number(left.balance || 0)
			break
		case "created_asc":
			result = getCreatedTime(left.created_at) - getCreatedTime(right.created_at)
			break
		case "created_desc":
			result = getCreatedTime(right.created_at) - getCreatedTime(left.created_at)
			break
	}

	return result || compareText(left.name, right.name) || compareText(left.uid, right.uid)
}

export const filterAndSortCompanies = <T extends CompanyDirectoryEntry>(
	companies: readonly T[],
	filter: CompanyDirectoryFilter
): T[] => {
	const query = normalize(filter.query)

	return companies
		.filter((company) => {
			if (filter.accountType !== "all" && getAccountType(company) !== filter.accountType) return false
			if (filter.status !== "all" && company.status !== filter.status) return false
			if (filter.registrationSource !== "all" && company.registration_source !== filter.registrationSource) return false
			if (filter.providerCode !== "all" && !company.issuers?.some((issuer) => issuer.provider_code === filter.providerCode)) return false
			if (!query) return true

			return [
				company.name,
				company.uid,
				company.number,
				company.vat_number,
				company.referral_code,
				...(company.admins ?? []).flatMap((admin) => [admin.name, admin.email])
			]
				.some((value) => normalize(value).includes(query))
		})
		.sort((left, right) => compareCompanies(left, right, filter.sort))
}
